import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const UserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '', login: '', password: '', roles: 'user', permissions: 'ALL', status: 'active',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionType, setPermissionType] = useState('all');
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '', login: user.login || '', password: '',
        roles: user.roles || 'user', permissions: user.permissions || 'ALL', status: user.status || 'active',
      });
      setPermissionType(user.permissions === 'ALL' ? 'all' : 'specific');
      if (user.permissions && user.permissions !== 'ALL') {
        setSelectedProjects(user.permissions.split(';').map(p => p.trim()).filter(Boolean));
      }
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(response.data.map(p => ({ name: p.name, displayName: p.displayName || '' })));
    } catch (err) { console.error('Error fetching projects:', err); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionTypeChange = (type) => {
    setPermissionType(type);
    if (type === 'all') {
      setFormData(prev => ({ ...prev, permissions: 'ALL' }));
      setSelectedProjects([]);
    } else {
      setFormData(prev => ({ ...prev, permissions: selectedProjects.join(';') }));
    }
  };

  const handleProjectSelect = (projectName) => {
    if (!selectedProjects.includes(projectName)) {
      const newSelected = [...selectedProjects, projectName];
      setSelectedProjects(newSelected);
      setFormData(prev => ({ ...prev, permissions: newSelected.join(';') }));
    }
    setProjectSearch('');
    setShowProjectDropdown(false);
  };

  const handleProjectRemove = (projectName) => {
    const newSelected = selectedProjects.filter(p => p !== projectName);
    setSelectedProjects(newSelected);
    setFormData(prev => ({ ...prev, permissions: newSelected.length > 0 ? newSelected.join(';') : '' }));
  };

  const getProjectLabel = (projectName) => {
    const proj = projects.find(p => p.name === projectName);
    return proj?.displayName || projectName;
  };

  const filteredProjects = projects.filter(
    p => {
      const searchLower = projectSearch.toLowerCase();
      const matchesSearch = p.name.toLowerCase().includes(searchLower) || (p.displayName && p.displayName.toLowerCase().includes(searchLower));
      return matchesSearch && !selectedProjects.includes(p.name);
    }
  );

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.email.trim()) { setError('Email is required'); return; }
    if (!validateEmail(formData.email)) { setError('Invalid email format'); return; }
    if (!formData.login.trim()) { setError('Login is required'); return; }
    if (!user && !formData.password.trim()) { setError('Password is required for new users'); return; }
    if (formData.password && formData.password.length < 4) { setError('Password must be at least 4 characters'); return; }
    if (permissionType === 'specific' && !formData.permissions.trim()) { setError('Please specify at least one project'); return; }

    setLoading(true);
    try {
      const dataToSave = { ...formData };
      if (user && !dataToSave.password) delete dataToSave.password;
      await onSave(dataToSave);
    } catch (err) {
      setError(err.message || 'Failed to save user');
      setLoading(false);
    }
  };

  const inputClass = "bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500";
  const labelClass = "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/50 dark:bg-gray-900/80" onClick={onClose}>
      <div className="relative w-full max-w-2xl max-h-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-800">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {user ? 'Edit User' : 'Add New User'}
            </h3>
            <button onClick={onClose} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{error}</div>
              )}

              <div>
                <label htmlFor="email" className={labelClass}>Email *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="user@example.com" className={inputClass} autoComplete="off" />
              </div>

              <div>
                <label htmlFor="login" className={labelClass}>Login *</label>
                <input type="text" id="login" name="login" value={formData.login} onChange={handleChange} placeholder="username" className={inputClass} autoComplete="off" />
              </div>

              <div>
                <label htmlFor="password" className={labelClass}>Password {user ? '(leave empty to keep current)' : '*'}</label>
                <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} placeholder={user ? '••••••••' : 'Enter password'} className={inputClass} autoComplete="new-password" />
              </div>

              <div>
                <label htmlFor="roles" className={labelClass}>Role *</label>
                <select id="roles" name="roles" value={formData.roles} onChange={handleChange} className={inputClass}>
                  <option value="user">User</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.roles === 'admin' && 'Full access to Admin Panel and user management.'}
                  {formData.roles === 'moderator' && 'Can view projects and upload ZIP archives.'}
                  {formData.roles === 'user' && 'Can view assigned projects.'}
                </p>
              </div>

              <div>
                <label className={labelClass}>Status</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={formData.status === 'active'} onChange={() => setFormData(prev => ({ ...prev, status: prev.status === 'active' ? 'inactive' : 'active' }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">{formData.status === 'active' ? 'Active' : 'Inactive'}</span>
                </label>
              </div>

              <div>
                <label className={labelClass}>Project Access *</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 mb-3">
                  <button type="button" onClick={() => handlePermissionTypeChange('all')}
                    className={`flex-1 py-2 text-sm font-medium ${permissionType === 'all' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}>
                    All Projects
                  </button>
                  <button type="button" onClick={() => handlePermissionTypeChange('specific')}
                    className={`flex-1 py-2 text-sm font-medium ${permissionType === 'specific' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}>
                    Specific Projects
                  </button>
                </div>

                {permissionType === 'specific' && (
                  <div className="space-y-2">
                    {selectedProjects.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedProjects.map((project) => (
                          <span key={project} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300">
                            {getProjectLabel(project)}
                            <button type="button" onClick={() => handleProjectRemove(project)} className="ml-1 text-primary-600 hover:text-primary-800 dark:text-primary-400">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="relative">
                      <input type="text" value={projectSearch}
                        onChange={(e) => { setProjectSearch(e.target.value); setShowProjectDropdown(true); }}
                        onFocus={() => setShowProjectDropdown(true)}
                        placeholder="Search and select projects..."
                        className={inputClass} autoComplete="off" />
                      {showProjectDropdown && filteredProjects.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600 max-h-48 overflow-y-auto">
                          {filteredProjects.map((project) => (
                            <button key={project.name} type="button" onClick={() => handleProjectSelect(project.name)}
                              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600">
                              {project.displayName || project.name}
                              {project.displayName && <span className="ml-1 text-xs text-gray-400">({project.name})</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600">
              <button type="submit" disabled={loading}
                className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50">
                {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
              </button>
              <button type="button" onClick={onClose}
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
